import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthContext } from '../../auth/AuthContext';
import type { FormConfig } from '../../config/types';
import { unsubmitForm } from '../../service/form';
import { Query } from '../../types';
import { resolveSelectedFormId, setSelectedFormIdInSession } from '../../utils/form-id';
import { londonNow, parseLondon } from '../../utils/londonTime';
import { getTenantFromPath } from '../../utils/tenant-utils';

type Props = {
  allowUnlock?: FormConfig['allowUnlock'];
  submittedAt?: string;
  formCloseAt?: string;
  formId?: string;
};

const MS_IN_DAY = 1000 * 60 * 60 * 24;

const ConfirmBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: rgba(11, 12, 12, 0.65);
`;

const ConfirmDialog = styled.div`
  width: min(720px, 100%);
  max-height: min(80vh, 900px);
  overflow: auto;
  background: #ffffff;
  border: 3px solid #0b0c0c;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;

const parseAllowUnlock = (allowUnlock?: FormConfig['allowUnlock']) => {
  if (typeof allowUnlock === 'number') {
    return { type: 'daysFromSubmit' as const, days: allowUnlock };
  }

  if (typeof allowUnlock === 'string') {
    const trimmed = allowUnlock.trim();

    if (/^formclose/i.test(trimmed)) {
      const match = trimmed.match(/formclose\s*(\+|-)\s*(\d+)/i);
      const offset = match ? Number(`${match[1]}${match[2]}`) : 0;
      return { type: 'formClose' as const, offset };
    }

    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return { type: 'daysFromSubmit' as const, days: numeric };
    }
  }

  return null;
};

const toDate = (value?: string) => parseLondon(value ?? undefined);

const computeUnlockDeadline = (
  allowUnlock: FormConfig['allowUnlock'],
  submittedAt?: string,
  formCloseAt?: string,
) => {
  const parsed = parseAllowUnlock(allowUnlock);
  if (!parsed) return null;

  if (parsed.type === 'daysFromSubmit') {
    const submitted = toDate(submittedAt);
    if (!submitted) return null;
    return new Date(submitted.getTime() + parsed.days * MS_IN_DAY);
  }

  const closeDate = toDate(formCloseAt);
  if (!closeDate) return null;
  return new Date(closeDate.getTime() + parsed.offset * MS_IN_DAY);
};

const hasExplicitDeadlineTime = (value?: string) =>
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(String(value || '').trim());

const UnlockNotice = ({ allowUnlock, submittedAt, formCloseAt, formId }: Props) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [hasConfirmedResubmit, setHasConfirmedResubmit] = useState(false);
  const deadline = computeUnlockDeadline(allowUnlock, submittedAt, formCloseAt);
  if (!deadline) return null;

  const nowDate = londonNow();
  const parts = (date: Date) => {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (type: string) => fmt.find((p) => p.type === type)?.value || '00';
    return {
      y: Number(get('year')),
      m: Number(get('month')),
      d: Number(get('day')),
    };
  };
  const today = parts(nowDate);
  const deadlineParts = parts(deadline);
  const todayStartUtc = Date.UTC(today.y, today.m - 1, today.d);
  const deadlineStartUtc = Date.UTC(deadlineParts.y, deadlineParts.m - 1, deadlineParts.d);
  const remainingDays = Math.max(0, Math.floor((deadlineStartUtc - todayStartUtc) / MS_IN_DAY));
  const expired = hasExplicitDeadlineTime(formCloseAt)
    ? nowDate.getTime() >= deadline.getTime()
    : remainingDays <= 0;
  const { fetchJWT } = useAuthContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const tenant = getTenantFromPath(window.location.pathname);
  const myFormsPath = tenant ? `/${tenant}?my_forms=1&admin=0` : '/?my_forms=1&admin=0';
  const selectedFormIdFromContext = resolveSelectedFormId(tenant, location?.search || '');
  const selectedFormId = selectedFormIdFromContext || String(formId || '').trim() || undefined;
  const indexPath = tenant ? `/${tenant}` : '/';
  const indexPathWithFormId = selectedFormId ? `${indexPath}?formId=${selectedFormId}` : indexPath;

  const { mutateAsync: unsubmit, isPending: isUpdating } = useMutation({
    mutationFn: async () => {
      const { idToken } = await fetchJWT();
      if (!idToken) throw new Error('Missing auth token');
      return unsubmitForm(
        idToken,
        undefined,
        selectedFormId ? { form_id: selectedFormId } : undefined,
      );
    },
    onSuccess: async () => {
      setIsConfirmModalOpen(false);
      setHasConfirmedResubmit(false);
      await queryClient.invalidateQueries({ queryKey: [Query.FormData] });
      setSelectedFormIdInSession(tenant, selectedFormId);
      navigate(indexPathWithFormId);
    },
  });

  const handleCloseConfirmModal = () => {
    if (isUpdating) return;
    setIsConfirmModalOpen(false);
    setHasConfirmedResubmit(false);
  };

  return (
    <>
      <section className="govuk-!-margin-top-4 govuk-!-margin-bottom-4">
        <h3 className="govuk-heading-m">Update your form</h3>
        <p className="govuk-body govuk-!-margin-bottom-1">Time remaining: {remainingDays} days</p>
        {expired ? (
          <p className="govuk-body govuk-!-margin-top-2">
            The deadline to edit this form has passed. You can no longer update your answers.
          </p>
        ) : (
          <>
            <p className="govuk-body govuk-!-margin-top-2">
              You can update this form to make changes.{' '}
              <strong>Any updates won't be recorded until you resubmit.</strong> When you resubmit,
              your previous submission is replaced in full. If you don't resubmit, your earlier
              submission remains.
            </p>
            <div className="govuk-button-group">
              <button
                type="button"
                className="govuk-button"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => {
                  setSelectedFormIdInSession(tenant, null);
                  navigate(myFormsPath);
                }}
              >
                Goto My Forms
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => setIsConfirmModalOpen(true)}
              >
                Update Form
              </button>
            </div>
          </>
        )}
      </section>
      {isConfirmModalOpen && (
        <ConfirmBackdrop>
          <ConfirmDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="update-form-confirm-title"
          >
            <h2 id="update-form-confirm-title" className="govuk-heading-m govuk-!-margin-bottom-4">
              Update your form
            </h2>
            <p className="govuk-body govuk-!-margin-bottom-6">
              You can unsubmit this form to make changes.{' '}
              <strong>Any edits won't be recorded until you resubmit.</strong> When you resubmit,
              your previous submission is replaced in full. If you don't resubmit, your earlier
              submission remains.
            </p>
            <div className="govuk-form-group govuk-!-margin-bottom-6">
              <div className="govuk-checkboxes" data-module="govuk-checkboxes">
                <div className="govuk-checkboxes__item">
                  <input
                    id="update-form-resubmit-confirmation"
                    className="govuk-checkboxes__input"
                    type="checkbox"
                    checked={hasConfirmedResubmit}
                    onChange={(event) => setHasConfirmedResubmit(event.target.checked)}
                    disabled={isUpdating}
                  />
                  <label
                    className="govuk-label govuk-checkboxes__label"
                    htmlFor="update-form-resubmit-confirmation"
                  >
                    I understand I must resubmit my form or my old answers will be submitted
                  </label>
                </div>
              </div>
            </div>
            <div className="govuk-button-group govuk-!-margin-bottom-0">
              <button
                type="button"
                className="govuk-button"
                data-module="govuk-button"
                data-prevent-double-click="true"
                disabled={!hasConfirmedResubmit || isUpdating}
                onClick={() => void unsubmit()}
              >
                Continue
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={handleCloseConfirmModal}
                disabled={isUpdating}
              >
                Cancel
              </button>
            </div>
          </ConfirmDialog>
        </ConfirmBackdrop>
      )}
    </>
  );
};

export default UnlockNotice;
